import { useState } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { Formik, Form } from 'formik'
import { Response } from 'request-dot-js'

import { authFromResponse } from 'utils'
import { RegistrationType } from '../../utils'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { UserClient } from 'api/clients/UserClient'

import Styles from './EnterPassword.module.scss'
import Shared from '../../Shared.module.scss'
import { CustomDomainWarning } from '../CustomDomainWarning/CustomDomainWarning'
import { CustomDomainError } from '../CustomDomainError/CustomDomainError'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { useQueryParams } from 'hooks/useQueryParams'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { GhostButton } from 'components/Buttons/GhostButton'
import { Button } from 'blaze-ui/Button'
import { useQueryClient } from 'react-query'
import { useIsBlaze } from 'hooks/useIsBlaze'
import ImageAssembly from '../Blaze/Testimonials/CartoonTestimonials/ImageAssembly'
import { Testimonials } from '../Blaze/Testimonials/Testimonials'
import { ArrowRight } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import CompanyLogo from 'components/CompanyLogo/CompanyLogo'

type Props = {
  email: string
  transitionPage: (page: string) => void
  redirectTo: string
  workspaceInviteToken: string
}

const EnterPassword = ({ email: emailProp, transitionPage, redirectTo, workspaceInviteToken }: Props) => {
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState(emailProp)
  const [loginFailed, setLoginFailed] = useState(false)
  const [customDomainError, setCustomDomainError] = useState<Response | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { domain: customDomain, promotion_code: promotionCode } = useQueryParams()
  const queryClient = useQueryClient()
  const isBlaze = useIsBlaze()

  const { onLoginOrSignUpSuccess: onLoginSuccess } = useLoginOrSignUp()

  const resetPassword = async () => {
    await UserClient.sendEmail(email, 'password_reset', redirectTo, workspaceInviteToken)
    transitionPage('RESET_PASSWORD')
  }

  const login = async () => {
    setIsProcessing(true)

    const res = await client.login(email, password, customDomain)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const auth = authFromResponse(res.data.data, res.headers)

      const nextPage = await onLoginSuccess(
        auth,
        RegistrationType.EmailAndPassword,
        redirectTo,
        workspaceInviteToken,
        promotionCode,
      )

      queryClient.invalidateQueries('featureFlags')

      setIsProcessing(false)

      if (nextPage) transitionPage(nextPage)
    } else {
      if (customDomain) {
        setCustomDomainError(res)
      } else {
        setLoginFailed(true)
      }
      setIsProcessing(false)
      return
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Do not unfocus if we are clicking submit button w/in the text input
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) return
    setShowPassword(false)
  }

  return (
    <div data-testid="enterPassword" className={cx(Shared.content, { [Shared.isBlaze]: isBlaze })}>
      <div className={Shared.leftPanel}>
        <div className={Shared.header}>
          <GhostButton
            className={cx(Styles.backButton, { [Shared.isBlaze]: isBlaze })}
            aria-label="Back"
            onClick={() => transitionPage('GO_BACK')}
          >
            <LeftArrowIcon />
          </GhostButton>
          {isMobile && (
            <div className={Styles.companyLogo}>
              <CompanyLogo small={true} red />
            </div>
          )}
        </div>
        <div className={cx(Shared.largerLeftContent, Styles.main)}>
          <div
            className={cx(Styles.title, {
              [Styles.withoutSubTitleOrErrors]: !customDomain,
              [Shared.isBlaze]: isBlaze,
            })}
          >
            <Heading level={1}>Enter password</Heading>
          </div>
          <CustomDomainWarning />
          <Formik initialValues={{ password: '' }} onSubmit={login}>
            {() => {
              return (
                <Form className={Shared.form}>
                  <div className={Styles.formGap}>
                    <Text variant="label">Email Address</Text>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      disabled={true}
                      size="xl"
                      name="email"
                      value={email}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        if (loginFailed) {
                          setLoginFailed(false)
                        }
                        setEmail(event.target.value)
                      }}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className={Shared.newFieldWrapper}>
                    <div className={Styles.formGap}>
                      <Text variant="label">Password</Text>
                      <Input
                        name="password"
                        id="password"
                        autoComplete="current-password"
                        size="xl"
                        isValid={!loginFailed}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        placeholder="Password"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          if (loginFailed) {
                            setLoginFailed(false)
                          }
                          setPassword(event.target.value)
                        }}
                        autoFocus
                      />
                    </div>
                    {password && (
                      <div className={Shared.showPasswordButton}>
                        <Button variant="ghost" ignoreHover onPress={() => setShowPassword(!showPassword)}>
                          <Text variant="smallList" color="var(--dark-40)">
                            {showPassword ? 'Hide' : 'Show'}
                          </Text>
                        </Button>
                      </div>
                    )}
                  </div>
                  {loginFailed && (
                    <FormErrorMessage>
                      <div className={Shared.unauthorizedUser}>
                        Sorry, your password doesn't match. Try again, or{' '}
                        <button type="button" className={Styles.reset} onClick={resetPassword}>
                          Reset password
                        </button>
                      </div>
                    </FormErrorMessage>
                  )}
                  {customDomainError && <CustomDomainError error={customDomainError} customDomain={customDomain} />}
                  <div className={Styles.buttonContainer}>
                    <Button
                      type="submit"
                      isDisabled={isProcessing}
                      endIcon={ArrowRight}
                      fullWidth={isMobile}
                      variant="primary"
                      size="lg"
                    >
                      <span>Continue</span>
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
          <div className={Styles.resetPassword}>
            <span className={Shared.linkWrapperText}>
              Forgot your password?{' '}
              <button className={Shared.link} onClick={resetPassword}>
                Reset it
              </button>
            </span>
          </div>
          <div className={Styles.testimonialContainer}>
            {isMobile && <Testimonials currentPage="continueWithEmailOrGmail" noMargin newFormat />}
          </div>
        </div>
      </div>
      {!isTabletPortrait && !isMobile && <ImageAssembly offsetTop={200} useSmallerBubble page={'enterPassword'} />}
    </div>
  )
}

export default EnterPassword
